import * as units from '@core_n/common/units';
import * as utils from '@core_n/common/utils';
import { Source } from '@core_n/common/enums';
import { SensorCoreObject } from '@core_n/asp/SensorCoreObject';
import { IObjectsPool } from '@core_n/asp/interfaces';
import { Location } from '@core_n/common/Location';
import * as consts from '@core_n/common/consts';
import { SensorBiasMngr } from '../sensor-bias/sensor-bias';
import { userSettings } from '@core_n/common/settings';
import { AppConfig } from '../../core/app/app.config';

const SENSOR_WITH_BIAS_COLOR = [0.90, 0.90, 0.00]; /* yellow */
const SENSOR_REGISTERED_COLOR = [0.10, 0.80, 0.00]; /* green */

export var NAT_NAI_COUNT = 20;
export const NAINAT_SYMBOL = "zones-icon";
export const NAINAT_SYMBOL_CSS = utils.getPlaneSymbol(NAINAT_SYMBOL);

export class SensorCoverage {

  private arc: any = null;
  private fromLine: any = null;
  private toLine: any = null;
  private circle: any = null;

  shape: any; // arc + connecting lines, or circle

  constructor() {
    this.setArcShape();
  }

  setSensorLocation(geometry) {
    if (this.arc != null) {
      this.fromLine.points[0].x = geometry.x;
      this.fromLine.points[0].y = geometry.y;
      this.fromLine.points[0].z = geometry.z;
      this.toLine.points[0].x = geometry.x;
      this.toLine.points[0].y = geometry.y;
      this.toLine.points[0].z = geometry.z;
      this.arc.x = geometry.x;
      this.arc.y = geometry.y;
      this.arc.setZ(geometry.z);
    }
    else {
      this.circle.x = geometry.x;
      this.circle.y = geometry.y;
      this.circle.setZ(geometry.z);
    }
  }

  setCoverage(location: any, minDeg: number, maxDeg: number) {

    let distance = 10000;
    // 10000 meters need to make param when this thing will actually be in use...
    let origin = {
      x: location.x,
      y: location.y,
      z: location.z
    };

    if (minDeg != maxDeg && maxDeg - minDeg < 360) {
      this.setArcShape();
      this.setSensorLocation(origin);

      this.arc.radius = utils.distanceMeterToDeg(origin, distance);
      this.arc.setFromToAnglesDegrees(minDeg, maxDeg);

      let arcPoints = this.arc.getDrawingPoints();

      this.fromLine.points[1] = arcPoints[0];
      this.toLine.points[1] = arcPoints[arcPoints.length - 1];
    }
    else {
      // this.setCircleShape();
      // this.setSensorLocation(origin);

      // this.circle.radius = utils.distanceMeterToDeg(origin, distance);
      this.shape = null;

    }
  }

  setArcShape() {
    this.circle = null;
    this.arc = new NeWMI.Geometry.Arc();
    this.fromLine = new NeWMI.Geometry.Polyline();
    this.fromLine.points.push(new NeWMI.Geometry.Point());
    this.fromLine.points.push(new NeWMI.Geometry.Point());
    this.toLine = new NeWMI.Geometry.Polyline();
    this.toLine.points.push(new NeWMI.Geometry.Point());
    this.toLine.points.push(new NeWMI.Geometry.Point());
    this.shape = new NeWMI.Geometry.GeometryCollection();
    this.shape.addGeometry(this.fromLine);
    this.shape.addGeometry(this.toLine);
    this.shape.addGeometry(this.arc);
  }

  setCircleShape() {
    if (this.circle == null) {
      this.arc = null;
      this.circle = new NeWMI.Geometry.Circle();
      this.shape = new NeWMI.Geometry.GeometryCollection();
      this.shape = this.circle;
    }
  }
}

export class NaiNatAreaObject {
  naiOrNat: number;   // 1 - nai , 2 - nat
  sic: number;
  sac: number;
  naiNatId: number;
  operationAction: number;
  geometricShape: number;
  minAltitude: number;
  maxAltitude: number;
  circleLatitude: number;
  circleLongitude: number;
  circleRadius: number;
  radarStartAngle: number;
  radarEndAngle: number;
  radarRadius: number;
  polygonPoints: PolygonPoint[];
  naiNatShape: any;
  isVisible: boolean;

  isNew: boolean;
  needsUpdate: boolean;
}

class PolygonPoint {
  Lat: number;
  Long: number;
}

export class SensorObject extends SensorCoreObject {
  coverage: SensorCoverage = null;
  naiNatArray: Array<NaiNatAreaObject>;
  naiNatDeleteList: Array<Number>;
  naiNatAreaObject: NaiNatAreaObject = null;
  isNatNaiVisible = true;
  geometryMenuVisible = false;

  AcousticMinAngle: number = 0;
  AcousticMaxAngle: number = 360;

  showCoverage: boolean = false;
  azimuth?: number; // degrees, 0-360, 0 = north, clockwise
  isNewCamera: boolean = false;

  constructor(objectsPool: IObjectsPool, public sensorID: number, public sensorLetter: string) {
    super(objectsPool, sensorID, sensorLetter);

    if (!this.naiNatArray)
      this.naiNatArray = new Array();

    if (!this.naiNatDeleteList)
      this.naiNatDeleteList = new Array();
  }

  handleMsg(msgObj: any): boolean {
    if (msgObj.MessageType == consts.MSG_TYPE_SENSOR) {

      this.AcousticMinAngle = 0;
      this.AcousticMaxAngle = 360;

      let loc: any = { z: msgObj.LocationAltitude, x: msgObj.LocationLong/* * units.RAD_TO_DEG*/, y: msgObj.LocationLat/* * units.RAD_TO_DEG*/ };
      if (typeof loc != 'undefined') {
        if (!this.coverage) {
          this.coverage = new SensorCoverage();
          this.coverage.setCoverage(loc, 0, 0);
        }
      }
    }
    super.handleMsg(msgObj);
    return true;
  }

  handleVid(msgObj: any): boolean {
    this.AcousticMinAngle = 0;
    this.AcousticMaxAngle = 360;
    let loc: Location = { altitude: 300, longitude: msgObj.locationY ? msgObj.locationY : 0, latitude: msgObj.locationX ? msgObj.locationX : 0 };
    if (typeof loc != 'undefined') {
      if (!this.coverage) {
        this.coverage = new SensorCoverage();
        this.coverage.setCoverage(loc, 0, 0);
      }
    }
    super.handleVid(msgObj);
    return true;
  }

  getColor() {
    let sensorBias = SensorBiasMngr.getSelf().getSensorBias(this.sensorID);
    if (sensorBias) {
      if (sensorBias.isAzimuthRegistered && sensorBias.isElevationRegistered && sensorBias.isRangeRegistered && sensorBias.isLocationRegistered)
        return SENSOR_REGISTERED_COLOR;
      else
        return SENSOR_WITH_BIAS_COLOR;
    }
    else {
      return super.getColor();
    }
  }

  public updateNatNai(msgObj) {
    if (msgObj.MessageType == consts.MSG_TYPE_NAI) {
      if (!this.naiNatArray) {
        this.naiNatArray = new Array();
      }
      this.naiNatAreaObject = new NaiNatAreaObject();
      this.naiNatAreaObject.naiNatId = msgObj.NaiNatId;
      this.naiNatAreaObject.isVisible = true;

      if (msgObj.OperationAction != 2) {
        this.naiNatAreaObject.naiOrNat = msgObj.NaiOrNat;
        this.naiNatAreaObject.sic = msgObj.Sic;
        this.naiNatAreaObject.sac = msgObj.Sac;
        this.naiNatAreaObject.operationAction = msgObj.OperationAction;
        this.naiNatAreaObject.geometricShape = msgObj.GeometricShape;
        this.naiNatAreaObject.minAltitude = msgObj.MinAltitude;
        this.naiNatAreaObject.maxAltitude = msgObj.MaxAltitude;
        this.naiNatAreaObject.circleLatitude = msgObj.CircleLatitude * units.RAD_TO_DEG;
        this.naiNatAreaObject.circleLongitude = msgObj.CircleLongitude * units.RAD_TO_DEG;
        this.naiNatAreaObject.circleRadius = msgObj.CircleRadius;
        this.naiNatAreaObject.radarStartAngle = msgObj.RadarStartAngle;
        this.naiNatAreaObject.radarEndAngle = msgObj.RadarEndAngle;
        this.naiNatAreaObject.radarRadius = msgObj.RadarRadius;
        this.naiNatAreaObject.polygonPoints = msgObj.PolygonPoints;
        this.naiNatAreaObject.naiNatShape = this.getGeometryShape(this.naiNatAreaObject);
      }
      else {
        this.naiNatAreaObject.naiNatShape = null;
      }
      let isExistNatNai = false;
      this.naiNatArray.forEach((obj, index) => {
        if (obj.naiNatId == this.naiNatAreaObject.naiNatId) {
          if (msgObj.OperationAction == 2) {
            this.naiNatArray.splice(index, 1);
            this.naiNatDeleteList.push(this.naiNatAreaObject.naiNatId);
          }
          else {
            this.naiNatArray[index] = this.naiNatAreaObject;
          }

          isExistNatNai = true;
        }
      });
      if (msgObj.OperationAction == 0 && isExistNatNai == false) {
        this.naiNatAreaObject.isNew = true;
        this.naiNatArray.push(this.naiNatAreaObject);
      }
      else
        this.naiNatAreaObject.needsUpdate = true;
    
      this.postHandleMsg(msgObj);

      this.notifyDataChange();

      return true;
    }
  }
  protected getGeometryShape(alertPolygonObject) {
    switch (alertPolygonObject.geometricShape) {
      // Polygon
      case 0: {
        let polygonGeometry: any = new NeWMI.Geometry.Polygon();
        alertPolygonObject.polygonPoints.forEach(point => {
          polygonGeometry.points.push({ x: NeWMI.Service.Math.ConversionsSvc.toDegrees(point.Long), y: NeWMI.Service.Math.ConversionsSvc.toDegrees(point.Lat) });
        });
        return polygonGeometry;
      }
      // Ellipse
      case 1: {
        let EllipseGeometry: any = new NeWMI.Geometry.Ellipse();
        EllipseGeometry.x = alertPolygonObject.circleLongitude;
        EllipseGeometry.y = alertPolygonObject.circleLatitude;
        EllipseGeometry.xRadius = NeWMI.Service.Math.ConversionsSvc.simpleMetersToDegreesConversion(alertPolygonObject.circleRadius, EllipseGeometry.x, EllipseGeometry.y, true); //alertPolygonObject.circleRadius;
        EllipseGeometry.yRadius = NeWMI.Service.Math.ConversionsSvc.simpleMetersToDegreesConversion(alertPolygonObject.circleRadius, EllipseGeometry.x, EllipseGeometry.y); //alertPolygonObject.circleRadius;
        return EllipseGeometry;
      }
    }
  }

  updateCoverage() {
    this.coverage.setCoverage(this.geometry, this.AcousticMinAngle, this.AcousticMaxAngle);
    this.notifyDataChange();
  }

  // Get object title
  getTitle(): string {
    if (this.naiNatAreaObject)
      return (this.naiNatAreaObject.naiOrNat == 1 ? "Nai " : "Nat ") + this.naiNatAreaObject.naiNatId + " | Sensor " + this.sic + ' (' + this.sensorLetter + ')';
    else
      return super.getTitle();
  }

  // Override AspObject
  // color for newmi drawing
  // getColor() {
  //   return this.newmiColor;
  // }

  // color for css html 
  getCssColor() { // 1 nai 2 nat
    if (this.naiNatAreaObject)
      return this.naiNatAreaObject.naiOrNat == 1 ? userSettings.naiColor : userSettings.natColor;
    return super.getCssColor();
  }

  // gets the svg icon name
  getSymbol(ForceSnsSym: boolean = false) {
    if (this.naiNatAreaObject && !ForceSnsSym)
      return NAINAT_SYMBOL;
    return super.getSymbol();
  }

  // gets full path and the icon
  getSvgSymbol() {
    if (this.naiNatAreaObject)
      return NAINAT_SYMBOL_CSS;
    return super.getSvgSymbol();
  }
}